import { z } from 'zod'
import { errorParentIsRemoved, Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import type { Coassign_Target_Cost_Other_Actual_Set_Input } from '@/gql/graphql'
import { graphql } from '@/gql/gql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { formatISO } from 'date-fns'
import { pick, filterPick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'
import { isExist } from '@/lib/relation-graph'

const TABLE_NAME = 'target_cost_other_actual' as const

export const targetCostOtherActualTableSchema = z.object({
  id: z.string().max(21),
  target_cost_other_id: z
    .string()
    .max(21)
    .refine(isExist('target_cost_other'), errorParentIsRemoved),
  month: z.string(), // date型、月初め (YYYY-MM-DD)
  cost: z.number().min(0).max(1000000000).default(0)
})

const targetCostOtherActualTableColumns = Object.keys(
  targetCostOtherActualTableSchema.shape
) as Array<keyof typeof targetCostOtherActualTableSchema.shape>

// contextでproject_idを使用するため拡張（DBには保存されない）
export const targetCostOtherActualSchema = targetCostOtherActualTableSchema.extend({
  project_id: z.string().optional()
})

export type TargetCostOtherActual = z.input<typeof targetCostOtherActualSchema>

export class TargetCostOtherActualMutator extends Mutator<typeof targetCostOtherActualSchema> {
  static readonly stageableClassName = 'TargetCostOtherActualMutator'
  schema = targetCostOtherActualSchema
  table = TABLE_NAME

  setCost(value: number) {
    if (value === 0) {
      if (this.isNewEntity()) {
        delete this.modified.cost
      } else {
        this.delete()
      }
    } else {
      if (this.isDeleted) {
        super.reset()
      }
      this.modified.cost = value
    }
  }

  static async load(
    id: string,
    targetCostOtherId: string,
    projectId: string
  ): Promise<Reactive<TargetCostOtherActualMutator>> {
    const self = this.createInstance()

    const base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.context = { id, target_cost_other_id: targetCostOtherId, project_id: projectId }
    if (base !== undefined) {
      self.base = base
    }

    return self
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const queries: InsertQuery[] = []
    const tenant_id = useUserStore().userInfo!.tenantId
    const rawData = { ...(await this.parsed)!, ...{ tenant_id } }
    const sqliteParams = {
      ...filterPick(rawData, ...targetCostOtherActualTableColumns),
      ...{ tenant_id }
    }
    queries.push(DataBase.insertInto(this.table).values(sqliteParams).compile())
    return queries
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const target_cost_other_actual_id = this.context.id!
    const queries: UpdateQuery[] = []
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const sqliteParams = {
      ...filterPick(modifiedParams, ...targetCostOtherActualTableColumns)
    }
    queries.push(
      DataBase.updateTable(this.table)
        .set(sqliteParams)
        .where('id', '=', target_cost_other_actual_id)
        .compile()
    )

    return queries
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const target_cost_other_actual_id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', target_cost_other_actual_id).compile()]
  }

  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const rawData = { ...(await this.parsed)! }
    const params = { ...filterPick(rawData, ...targetCostOtherActualTableColumns) }

    return [
      {
        mutation: insertTargetCostOtherActualMutationDocument,
        variables: {
          input: {
            ...params,
            commit_id
          }
        }
      }
    ]
  }

  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const mutations: MutationOperationAndVariables[] = []
    const targetCostOtherActualId = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const modifiedParams = pick(
      rawData,
      Object.keys(this.modified) as (keyof typeof this.modified)[]
    )
    const params = { ...filterPick(modifiedParams, ...targetCostOtherActualTableColumns) }
    const variables: Coassign_Target_Cost_Other_Actual_Set_Input = { ...params, ...{ commit_id } }
    mutations.push({
      mutation: updateTargetCostOtherActualMutationDocument,
      variables: {
        id: targetCostOtherActualId,
        input: variables
      }
    })

    return mutations
  }

  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const deleted_at = formatISO(new Date())
    return [
      {
        mutation: updateDeletedAtTargetCostOtherActualMutationDocument,
        variables: { target_cost_other_actual_id: id, deletedAt: deleted_at, commit_id: commitId }
      }
    ]
  }
}

const insertTargetCostOtherActualMutationDocument = graphql(/* GraphQL */ `
  mutation insertTargetCostOtherActual($input: coassign_target_cost_other_actual_insert_input!) {
    insert_coassign_target_cost_other_actual_one(object: $input) {
      created_at
    }
  }
`)

const updateTargetCostOtherActualMutationDocument = graphql(/* GraphQL */ `
  mutation updateTargetCostOtherActual(
    $id: String!
    $input: coassign_target_cost_other_actual_set_input!
  ) {
    update_coassign_target_cost_other_actual_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)

const updateDeletedAtTargetCostOtherActualMutationDocument = graphql(/* GraphQL */ `
  mutation updateDeletedAtTargetCostOtherActual(
    $target_cost_other_actual_id: String!
    $deletedAt: timestamptz!
    $commit_id: String!
  ) {
    update_coassign_target_cost_other_actual(
      where: { id: { _eq: $target_cost_other_actual_id }, deleted_at: { _is_null: true } }
      _set: { deleted_at: $deletedAt, commit_id: $commit_id }
    ) {
      affected_rows
    }
  }
`)
